import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import type { ReactNode } from "react";
import type { Socket } from "socket.io-client";
import type { ChatMessage } from "@caratzon/domain";

type SocketContextValue = {
  socket: Socket | null;
  connected: boolean;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  sendMessage: (payload: {
    conversationId: string;
    type: string;
    body: string;
    metadata?: Record<string, unknown>;
  }) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  markRead: (conversationId: string) => void;
  onNewMessage: (handler: (message: ChatMessage & { authorName: string }) => void) => () => void;
  onTyping: (handler: (data: { conversationId: string; userId: string; name: string }) => void) => () => void;
  onStoppedTyping: (handler: (data: { conversationId: string; userId: string }) => void) => () => void;
};

const SocketContext = createContext<SocketContextValue | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    import("socket.io-client").then(({ io }) => {
      const s = io({
        withCredentials: true,
        transports: ["websocket", "polling"],
      });

      s.on("connect", () => setConnected(true));
      s.on("disconnect", () => setConnected(false));

      socketRef.current = s;
      setSocket(s);
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit("join_conversation", { conversationId });
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit("leave_conversation", { conversationId });
  }, []);

  const sendMessage = useCallback(
    (payload: {
      conversationId: string;
      type: string;
      body: string;
      metadata?: Record<string, unknown>;
    }) => {
      socketRef.current?.emit("send_message", payload);
    },
    [],
  );

  const startTyping = useCallback((conversationId: string) => {
    socketRef.current?.emit("typing_start", { conversationId });
  }, []);

  const stopTyping = useCallback((conversationId: string) => {
    socketRef.current?.emit("typing_stop", { conversationId });
  }, []);

  const markRead = useCallback((conversationId: string) => {
    socketRef.current?.emit("mark_read", { conversationId });
  }, []);

  const onNewMessage = useCallback(
    (handler: (message: ChatMessage & { authorName: string }) => void) => {
      socketRef.current?.on("new_message", handler);
      return () => {
        socketRef.current?.off("new_message", handler);
      };
    },
    [socket],
  );

  const onTyping = useCallback(
    (handler: (data: { conversationId: string; userId: string; name: string }) => void) => {
      socketRef.current?.on("user_typing", handler);
      return () => {
        socketRef.current?.off("user_typing", handler);
      };
    },
    [socket],
  );

  const onStoppedTyping = useCallback(
    (handler: (data: { conversationId: string; userId: string }) => void) => {
      socketRef.current?.on("user_stopped_typing", handler);
      return () => {
        socketRef.current?.off("user_stopped_typing", handler);
      };
    },
    [socket],
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        joinConversation,
        leaveConversation,
        sendMessage,
        startTyping,
        stopTyping,
        markRead,
        onNewMessage,
        onTyping,
        onStoppedTyping,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

const noopSocket: SocketContextValue = {
  socket: null,
  connected: false,
  joinConversation: () => {},
  leaveConversation: () => {},
  sendMessage: () => {},
  startTyping: () => {},
  stopTyping: () => {},
  markRead: () => {},
  onNewMessage: () => () => {},
  onTyping: () => () => {},
  onStoppedTyping: () => () => {},
};

export function useChatSocket() {
  const context = useContext(SocketContext);
  return context ?? noopSocket;
}
